import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDocumentReaderRun } from '@commenda/api/commenda-ai/document-reader/useDocumentReaderRun';

import {
  DocumentReadingRunStripHelper,
  type DocumentReadingRunActivity,
  type IDocumentReadingRunStripView,
} from './DocumentReadingRunStripHelper';
import {
  DocumentReadingTimeLeftHelper,
  type IDocumentReadingStepSample,
} from './DocumentReadingTimeLeftHelper';

/** One run's docked strip. It shows while something moves on the run, holds its last state a few
 * seconds once the run settles, then tucks away. A run already settled on the first look stays hidden
 * unless it started in this session. A run that has not loaded, or failed to load, shows nothing.
 * The finish messages are the dock's watcher's, so they do not depend on the strip showing. */
export function useDocumentReadingRunStrip(
  runId: string,
  isNewInSession: boolean
): {
  stripView: IDocumentReadingRunStripView | null;
  isVisible: boolean;
  onExited: () => void;
} {
  const { run } = useDocumentReaderRun(runId);
  const activity = useMemo(
    () => (run ? DocumentReadingRunStripHelper.getActivity(run) : null),
    [run]
  );
  // Kept after the run settles so its last state shows before it tucks away; null while hidden.
  const [shownActivity, setShownActivity] =
    useState<DocumentReadingRunActivity | null>(null);
  const [isTucking, setIsTucking] = useState(false);
  const [sample, setSample] = useState<IDocumentReadingStepSample | null>(null);
  const [seenAt, setSeenAt] = useState(0);
  const hasSeenRunRef = useRef(false);

  // Each new look at the run reads the clock, for the time left, and decides whether the strip shows.
  useEffect(() => {
    if (!run) return;
    const now = Date.now();
    const isFirstLook = !hasSeenRunRef.current;
    hasSeenRunRef.current = true;
    setSeenAt(now);
    setSample((previous) =>
      DocumentReadingTimeLeftHelper.nextStepSample(
        previous,
        DocumentReadingTimeLeftHelper.getStepMeasure(run),
        now
      )
    );
    if (activity) {
      setShownActivity(activity);
      setIsTucking(false);
    } else if (isFirstLook && isNewInSession) setShownActivity('reading');
  }, [run, activity, isNewInSession]);

  // A new move on the run clears the timer, so a write-back started right after reading keeps it up.
  const isSettled = shownActivity !== null && activity === null;
  useEffect(() => {
    if (!isSettled) return undefined;
    const timer = setTimeout(
      () => setIsTucking(true),
      DocumentReadingRunStripHelper.SETTLED_STRIP_VISIBLE_MS
    );
    return () => clearTimeout(timer);
  }, [isSettled]);

  const onExited = useCallback(() => {
    if (!isTucking) return;
    setShownActivity(null);
    setIsTucking(false);
  }, [isTucking]);

  const stripView = useMemo(
    () =>
      run && shownActivity
        ? DocumentReadingRunStripHelper.getStripView(
            run,
            activity ?? shownActivity,
            sample,
            seenAt
          )
        : null,
    [run, shownActivity, activity, sample, seenAt]
  );

  return { stripView, isVisible: !isTucking, onExited };
}
